import { styled } from '@mui/material/styles';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import Title from '../atoms/Title';

type LargeInfoCardProps = {
  title: string;
  color: 'primary' | 'secondary';
  iconName: 'fire' | 'text';
} & ({ listItems: string[]; type: 'list' } | { text: string; type: 'text' });

const LargeInfoCard = (props: LargeInfoCardProps) => {
  const { title, color, iconName, type } = props;

  const theme = useTheme();

  let icon;
  switch (iconName) {
    case 'fire':
      icon = <WhatshotIcon />;
      break;
    case 'text':
      icon = <FormatAlignLeftIcon />;
      break;
  }

  return (
    <Card>
      <TitleWrapper>
        <IconContainer color={color}>{icon}</IconContainer>
        <Title variant="heading7" textTitle={title} sx={{ textAlign: 'left' }} />
      </TitleWrapper>
      <ContentWrapper>
        {type === 'list' ? (
          <List>
            {props.listItems.map((item) => (
              <ListItem key={item}>
                <CheckCircleIcon sx={{ color: color === 'primary' ? theme.palette.red[40] : theme.palette.blue[40] }} />
                <Typography variant="body1Light">{item}</Typography>
              </ListItem>
            ))}
          </List>
        ) : (
          <Text variant="body1Light">{props.text}</Text>
        )}
      </ContentWrapper>
    </Card>
  );
};

const Card = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '16px',
  backgroundColor: theme.palette.neutral.white,
  borderRadius: '10px',
}));

const TitleWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
  marginBottom: '15px',
}));

const IconContainer = styled('div')<{ color: string }>(({ theme, color }) => ({
  backgroundColor: color === 'primary' ? theme.palette.red[20] : theme.palette.blue[40],
  borderRadius: '5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '46px',
  minHeight: '46px',
  padding: '10px',
  '& svg': {
    color: color === 'primary' ? theme.palette.red[60] : theme.palette.blue[60],
  },
}));

const ContentWrapper = styled('div')(({ theme }) => ({
  fontFamily: 'Poppins Light',
  fontSize: '14px',
  lineHeight: '19.6px',
  textAlign: 'left',
}));

const List = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
}));

const ListItem = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '14px',
}));

const Text: React.FC<TypographyProps> = styled(Typography)(() => ({
  whiteSpace: 'pre-wrap',
}));

export { LargeInfoCard };
